// src/types/index.ts
var MAX_CONNECTED_DRAFT_REFERENCES = 10;

// src/services/api.ts
var API_KEYS_STORAGE_KEY = "eidolon.web.apiKeys";
var LEGACY_API_KEYS_STORAGE_KEYS = ["bpui.web.apiKeys"];
var API_KEYS_HEADER = "X-BPUI-API-KEYS";
function getBrowserStorage() {
  const maybeStorage = globalThis.localStorage;
  return maybeStorage ?? null;
}
function encodeBase64(value) {
  const withBtoa = globalThis;
  if (typeof withBtoa.btoa === "function") {
    return withBtoa.btoa(value);
  }
  const bytes = new TextEncoder().encode(value);
  const lookup = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let result = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    const trio = a << 16 | b << 8 | c;
    result += lookup[trio >> 18 & 63];
    result += lookup[trio >> 12 & 63];
    result += i + 1 < bytes.length ? lookup[trio >> 6 & 63] : "=";
    result += i + 2 < bytes.length ? lookup[trio & 63] : "=";
  }
  return result;
}
function getBrowserApiKeysHeader() {
  const storage = getBrowserStorage();
  if (!storage) {
    return {};
  }
  try {
    for (const key of [API_KEYS_STORAGE_KEY, ...LEGACY_API_KEYS_STORAGE_KEYS]) {
      const raw = storage.getItem(key);
      if (!raw) {
        continue;
      }
      const parsed = JSON.parse(raw);
      const keys = Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => typeof value === "string" && value.trim().length > 0)
      );
      if (Object.keys(keys).length === 0) {
        return {};
      }
      return {
        [API_KEYS_HEADER]: encodeBase64(JSON.stringify(keys))
      };
    }
  } catch {
    return {};
  }
  return {};
}
var EidolonAPI = class {
  baseUrl;
  customApiBaseUrl = null;
  constructor(baseUrl = "/api") {
    this.baseUrl = baseUrl;
  }
  /**
   * Set a custom API base URL (for custom domain support)
   */
  setCustomApiBaseUrl(url) {
    this.customApiBaseUrl = url;
  }
  /**
   * Get the current API base URL
   */
  getApiBaseUrl() {
    return this.customApiBaseUrl || this.baseUrl;
  }
  buildHeaders(headers = {}, contentType) {
    const merged = new Headers(headers);
    Object.entries(getBrowserApiKeysHeader()).forEach(([key, value]) => {
      merged.set(key, value);
    });
    if (contentType && !merged.has("Content-Type")) {
      merged.set("Content-Type", contentType);
    }
    return merged;
  }
  async request(path, options = {}) {
    const response = await fetch(`${this.getApiBaseUrl()}${path}`, {
      ...options,
      headers: this.buildHeaders(options.headers, "application/json")
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new APIError(response.status, errorData.detail || "Request failed");
    }
    return response.json();
  }
  parseFilename(contentDisposition) {
    if (!contentDisposition) {
      return null;
    }
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }
    const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }
    const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
    return plainMatch?.[1]?.trim() ?? null;
  }
  async requestDownload(path, options = {}, fallbackError) {
    const response = await fetch(`${this.getApiBaseUrl()}${path}`, {
      ...options,
      headers: this.buildHeaders(options.headers)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new APIError(response.status, errorData.detail || fallbackError);
    }
    return {
      blob: await response.blob(),
      filename: this.parseFilename(response.headers.get("Content-Disposition")),
      contentType: response.headers.get("Content-Type")
    };
  }
  // ===========================================================================
  // Configuration
  // ===========================================================================
  async getConfig() {
    return this.request("/config");
  }
  async updateConfig(config) {
    return this.request("/config", {
      method: "POST",
      body: JSON.stringify(config)
    });
  }
  async testConnection(request) {
    return this.request("/config/test", {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async getThemes() {
    return this.request("/config/themes");
  }
  async createTheme(theme) {
    return this.request("/config/themes", {
      method: "POST",
      body: JSON.stringify(theme)
    });
  }
  async exportTheme(name) {
    return this.requestDownload(
      `/config/themes/${encodeURIComponent(name)}/export`,
      {},
      "Theme export failed"
    );
  }
  async importTheme(file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);
    if (options.conflict_strategy) {
      formData.append("conflict_strategy", options.conflict_strategy);
    }
    if (options.target_name) {
      formData.append("target_name", options.target_name);
    }
    const response = await fetch(`${this.getApiBaseUrl()}/config/themes/import`, {
      method: "POST",
      body: formData,
      headers: this.buildHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new APIError(response.status, errorData.detail || "Theme import failed");
    }
    return response.json();
  }
  async updateTheme(name, theme) {
    return this.request(`/config/themes/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(theme)
    });
  }
  async duplicateTheme(name, request) {
    return this.request(`/config/themes/${encodeURIComponent(name)}/duplicate`, {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async renameTheme(name, request) {
    return this.request(`/config/themes/${encodeURIComponent(name)}/rename`, {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async deleteTheme(name) {
    return this.request(`/config/themes/${encodeURIComponent(name)}`, {
      method: "DELETE"
    });
  }
  // ===========================================================================
  // Models
  // ===========================================================================
  async getModels(provider, refresh = false) {
    const query = refresh ? "?refresh=true" : "";
    return this.request(`/models/${provider}${query}`);
  }
  async refreshModels(provider) {
    return this.request(`/models/${provider}/refresh`, {
      method: "POST"
    });
  }
  // ===========================================================================
  // Templates
  // ===========================================================================
  async getTemplates() {
    return this.request("/templates");
  }
  async listTemplates() {
    return this.getTemplates();
  }
  async getTemplate(name) {
    return this.request(`/templates/${encodeURIComponent(name)}`);
  }
  async getTemplateBlueprintContents(name) {
    return this.request(
      `/templates/${encodeURIComponent(name)}/blueprint-contents`
    );
  }
  async createTemplate(template) {
    return this.request("/templates", {
      method: "POST",
      body: JSON.stringify(template)
    });
  }
  async updateTemplate(name, template) {
    return this.request(`/templates/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(template)
    });
  }
  async deleteTemplate(name) {
    return this.request(`/templates/${encodeURIComponent(name)}`, {
      method: "DELETE"
    });
  }
  async validateTemplate(name) {
    return this.request(`/templates/${encodeURIComponent(name)}/validate`);
  }
  async duplicateTemplate(name, request) {
    return this.request(`/templates/${encodeURIComponent(name)}/duplicate`, {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async exportTemplate(name) {
    return this.requestDownload(
      `/templates/${encodeURIComponent(name)}/export`,
      {},
      "Template export failed"
    );
  }
  async importTemplate(file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${this.getApiBaseUrl()}/templates/import`, {
      method: "POST",
      body: formData,
      headers: this.buildHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new APIError(response.status, errorData.detail || "Template import failed");
    }
    return response.json();
  }
  async generateSeeds(request) {
    return this.request("/seedgen", {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async getLineage() {
    return this.request("/lineage");
  }
  async validatePath(request) {
    return this.request("/validate/path", {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async validateDraft(draftId) {
    return this.request(`/validate/draft/${encodeURIComponent(draftId)}`);
  }
  // ===========================================================================
  // Generation (SSE Streaming)
  // ===========================================================================
  generate(request) {
    return new GenerationStream(
      `${this.getApiBaseUrl()}/generate/single`,
      request
    );
  }
  async generateAsset(request, signal) {
    return this.request("/generate/asset", {
      method: "POST",
      body: JSON.stringify(request),
      signal
    });
  }
  async finalizeGeneration(request, signal) {
    return this.request("/generate/finalize", {
      method: "POST",
      body: JSON.stringify(request),
      signal
    });
  }
  generateBatch(seeds, options) {
    return new GenerationStream(
      `${this.getApiBaseUrl()}/generate/batch`,
      { seeds, ...options }
    );
  }
  // ===========================================================================
  // Drafts
  // ===========================================================================
  async getDrafts(filters) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== void 0) {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, String(v)));
          } else {
            params.set(key, String(value));
          }
        }
      });
    }
    const query = params.toString();
    return this.request(`/drafts${query ? `?${query}` : ""}`);
  }
  async listDrafts(filters) {
    return this.getDrafts(filters);
  }
  async getDraft(draftId) {
    return this.request(`/drafts/${encodeURIComponent(draftId)}`);
  }
  async saveDraft(draftId, updates) {
    return this.request(`/drafts/${encodeURIComponent(draftId)}`, {
      method: "PUT",
      body: JSON.stringify(updates)
    });
  }
  async deleteDraft(draftId) {
    return this.request(`/drafts/${encodeURIComponent(draftId)}`, {
      method: "DELETE"
    });
  }
  async updateAsset(draftId, assetName, content) {
    return this.request(
      `/drafts/${encodeURIComponent(draftId)}/assets/${encodeURIComponent(assetName)}`,
      {
        method: "PUT",
        body: JSON.stringify({ content })
      }
    );
  }
  async updateMetadata(draftId, metadata) {
    return this.request(
      `/drafts/${encodeURIComponent(draftId)}/metadata`,
      {
        method: "PUT",
        body: JSON.stringify(metadata)
      }
    );
  }
  // ===========================================================================
  // Similarity
  // ===========================================================================
  async analyzeSimilarity(request) {
    return this.request("/similarity", {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  async compareCharacters(request) {
    return this.request("/similarity/compare", {
      method: "POST",
      body: JSON.stringify(request)
    });
  }
  // ===========================================================================
  // Offspring
  // ===========================================================================
  generateOffspring(request) {
    return new GenerationStream(
      `${this.getApiBaseUrl()}/offspring`,
      request
    );
  }
  // ===========================================================================
  // Export
  // ===========================================================================
  async exportDraft(request) {
    return this.requestDownload(
      "/export",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
      },
      "Export failed"
    );
  }
  async getExportPresets() {
    const response = await this.request("/export/presets");
    return Array.isArray(response) ? response : response.presets;
  }
  // ===========================================================================
  // Chat/Refinement
  // ===========================================================================
  chat(request) {
    return new GenerationStream(
      `${this.getApiBaseUrl()}/chat`,
      request
    );
  }
  refine(request) {
    return new GenerationStream(
      `${this.getApiBaseUrl()}/chat/refine`,
      request
    );
  }
  // ===========================================================================
  // Blueprints
  // ===========================================================================
  async getBlueprints() {
    return this.request("/blueprints");
  }
  async getBlueprint(path) {
    return this.request(`/blueprints/${encodeURIComponent(path)}`);
  }
  async updateBlueprint(path, content) {
    return this.request(`/blueprints/${encodeURIComponent(path)}`, {
      method: "PUT",
      body: JSON.stringify({ content })
    });
  }
};
var GenerationStream = class {
  constructor(url, body) {
    this.url = url;
    this.body = body;
  }
  controller = null;
  readers = /* @__PURE__ */ new Set();
  onComplete = null;
  onError = null;
  currentEventType = "chunk";
  currentDataLines = [];
  subscribe(callback) {
    this.readers.add(callback);
    return () => this.readers.delete(callback);
  }
  onComplete_(callback) {
    this.onComplete = callback;
    return this;
  }
  onError_(callback) {
    this.onError = callback;
    return this;
  }
  async start() {
    this.controller = new AbortController();
    try {
      const response = await fetch(this.url, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          ...getBrowserApiKeysHeader()
        }),
        body: JSON.stringify(this.body),
        signal: this.controller.signal
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const event = this.parseSSELine(line);
          if (event) {
            this.readers.forEach((cb) => cb(event));
            if (event.event === "complete" && this.onComplete) {
              this.onComplete(event.data);
            }
            if (event.event === "error" && this.onError) {
              this.onError(event.data.error);
            }
          }
        }
      }
      const trailingEvent = this.parseSSELine("");
      if (trailingEvent) {
        this.readers.forEach((cb) => cb(trailingEvent));
        if (trailingEvent.event === "complete" && this.onComplete) {
          this.onComplete(trailingEvent.data);
        }
        if (trailingEvent.event === "error" && this.onError) {
          this.onError(trailingEvent.data.error);
        }
      }
    } catch (err) {
      if (this.onError) {
        this.onError(err instanceof Error ? err.message : "Stream error");
      }
    }
  }
  abort() {
    this.controller?.abort();
  }
  parseSSELine(line) {
    if (line.startsWith("event:")) {
      this.currentEventType = line.slice(6).trim();
      return null;
    }
    if (line.startsWith("data:")) {
      this.currentDataLines.push(line.slice(5).trim());
      return null;
    }
    if (line.trim() === "" && this.currentDataLines.length > 0) {
      try {
        const event = {
          event: this.currentEventType,
          data: JSON.parse(this.currentDataLines.join("\n"))
        };
        this.currentEventType = "chunk";
        this.currentDataLines = [];
        return event;
      } catch {
        this.currentEventType = "chunk";
        this.currentDataLines = [];
        return null;
      }
    }
    return null;
  }
};
var APIError = class extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
    this.name = "APIError";
  }
};
var api = new EidolonAPI();

// src/llm/types.ts
var ProviderEndpoints = {
  openai: "https://api.openai.com/v1",
  google: "https://generativelanguage.googleapis.com/v1beta",
  openrouter: "https://openrouter.ai/api/v1",
  anthropic: "https://api.anthropic.com/v1",
  deepseek: "https://api.deepseek.com",
  zai: "https://open.bigmodel.cn/api/paas/v4",
  moonshot: "https://api.moonshot.cn/v1",
  ollama: "http://localhost:11434/v1"
};
var ProviderAuthHeaders = {
  openai: "Authorization",
  google: "x-goog-api-key",
  openrouter: "Authorization",
  anthropic: "x-api-key",
  deepseek: "Authorization",
  zai: "Authorization",
  moonshot: "Authorization",
  ollama: ""
  // No auth required for local Ollama
};
function formatAuthHeader(provider, apiKey) {
  switch (provider) {
    case "openai":
    case "openrouter":
    case "deepseek":
    case "zai":
    case "moonshot":
      return `Bearer ${apiKey}`;
    case "google":
    case "anthropic":
      return apiKey;
    case "ollama":
      return "";
    // No auth required for local Ollama
    default:
      return apiKey;
  }
}
function detectProviderFromModel(model) {
  const modelLower = model.toLowerCase();
  if (modelLower.startsWith("openrouter/")) return "openrouter";
  if (modelLower.startsWith("openai/")) return "openai";
  if (modelLower.startsWith("google/")) return "google";
  if (modelLower.startsWith("anthropic/")) return "anthropic";
  if (modelLower.startsWith("deepseek/")) return "deepseek";
  if (modelLower.startsWith("zai/")) return "zai";
  if (modelLower.startsWith("moonshot")) return "moonshot";
  if (modelLower.startsWith("ollama/")) return "ollama";
  if (modelLower.startsWith("gpt-") || modelLower.startsWith("o1")) {
    return "openai";
  }
  if (modelLower.startsWith("gemini")) {
    return "google";
  }
  if (modelLower.startsWith("claude")) {
    return "anthropic";
  }
  if (modelLower.startsWith("llama") || modelLower.startsWith("mistral") || modelLower.startsWith("codellama") || modelLower.startsWith("vicuna") || modelLower.startsWith("qwen") || modelLower.startsWith("phi") || modelLower.startsWith("gemma") || modelLower.startsWith("starcoder") || modelLower.includes("ollama")) {
    return "ollama";
  }
  return "openrouter";
}

// src/llm/openai-compat.ts
function normalizeUsage(usage) {
  if (usage?.prompt_tokens === void 0 || usage.completion_tokens === void 0 || usage.total_tokens === void 0) {
    return void 0;
  }
  return {
    promptTokens: usage.prompt_tokens,
    completionTokens: usage.completion_tokens,
    totalTokens: usage.total_tokens
  };
}
var OpenAICompatEngine = class {
  config;
  constructor(config) {
    this.config = {
      temperature: 0.7,
      maxTokens: 4096,
      timeout: 12e4,
      ...config
    };
  }
  getProvider() {
    return this.config.provider;
  }
  getModel() {
    return this.config.model;
  }
  async generate(messages, options) {
    const temperature = options?.temperature ?? this.config.temperature;
    const maxTokens = options?.maxTokens ?? this.config.maxTokens;
    const response = await fetch(this.config.baseUrl + "/chat/completions", {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({
        model: this.config.model,
        messages,
        temperature,
        max_tokens: maxTokens,
        stream: false,
        ...this.buildExtraParams(options)
      }),
      signal: options?.signal
    });
    if (!response.ok) {
      const error = await this.parseErrorResponse(response);
      throw new Error(error);
    }
    const responseJson = await response.json();
    const data = responseJson;
    const content = data?.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error("No content in response");
    }
    return {
      content,
      finishReason: data?.choices?.[0]?.finish_reason,
      usage: normalizeUsage(data.usage)
    };
  }
  async *generateStream(messages, options) {
    const temperature = options?.temperature ?? this.config.temperature;
    const maxTokens = options?.maxTokens ?? this.config.maxTokens;
    const response = await fetch(this.config.baseUrl + "/chat/completions", {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({
        model: this.config.model,
        messages,
        temperature,
        max_tokens: maxTokens,
        stream: true,
        ...this.buildExtraParams(options)
      }),
      signal: options?.signal
    });
    if (!response.ok) {
      const error = await this.parseErrorResponse(response);
      throw new Error(error);
    }
    if (!response.body) {
      throw new Error("No response body");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6);
            if (dataStr.trim() === "[DONE]") {
              yield { content: "", done: true };
              return;
            }
            try {
              const data = JSON.parse(dataStr);
              const delta = data.choices?.[0]?.delta;
              if (delta?.content) {
                yield { content: delta.content, done: false };
              }
              if (delta?.tool_calls) {
                yield { content: JSON.stringify({ tool_calls: delta.tool_calls }), done: false };
              }
            } catch {
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
  async testConnection() {
    const start = performance.now();
    try {
      const response = await fetch(this.config.baseUrl + "/chat/completions", {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify({
          model: this.config.model,
          messages: [{ role: "user", content: "test" }],
          max_tokens: 5,
          stream: false
        })
      });
      const latency = performance.now() - start;
      if (!response.ok) {
        const error = await this.parseErrorResponse(response);
        return {
          success: false,
          error,
          modelInfo: {
            name: this.config.model
          }
        };
      }
      return {
        success: true,
        latencyMs: Math.round(latency),
        modelInfo: {
          name: this.config.model
        }
      };
    } catch (e) {
      return {
        success: false,
        error: e instanceof Error ? e.message : "Unknown error",
        modelInfo: {
          name: this.config.model
        }
      };
    }
  }
  buildHeaders() {
    const headers = {
      "Content-Type": "application/json"
    };
    if (this.config.apiKey) {
      headers["Authorization"] = "Bearer " + this.config.apiKey;
    }
    if (this.config.baseUrl.includes("openrouter.ai")) {
      headers["HTTP-Referer"] = "https://github.com/maeveoffae/eidolon-simulacra";
      headers["X-OpenRouter-Title"] = "Eidolon Simulacra";
    }
    return headers;
  }
  buildExtraParams(options) {
    const params = {};
    if (options?.topP !== void 0) params.top_p = options.topP;
    if (options?.frequencyPenalty !== void 0) params.frequency_penalty = options.frequencyPenalty;
    if (options?.presencePenalty !== void 0) params.presence_penalty = options.presencePenalty;
    return params;
  }
  async parseErrorResponse(response) {
    try {
      const data = await response.json();
      if (typeof data.error === "object" && data.error?.message) {
        return data.error.message;
      }
      if (data.error) {
        return String(data.error);
      }
      return "HTTP " + response.status;
    } catch {
      return "HTTP " + response.status;
    }
  }
};
async function listModels(baseUrl, apiKey) {
  const headers = {};
  if (apiKey) {
    headers["Authorization"] = "Bearer " + apiKey;
  }
  if (baseUrl.includes("openrouter.ai")) {
    headers["HTTP-Referer"] = "https://github.com/maeveoffae/eidolon-simulacra";
    headers["X-OpenRouter-Title"] = "Eidolon Simulacra";
  }
  const response = await fetch(baseUrl + "/models", {
    method: "GET",
    headers
  });
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw new Error("Failed to list models: HTTP " + response.status);
  }
  const responseJson = await response.json();
  const data = responseJson;
  const dataData = data.data;
  if (dataData && Array.isArray(dataData)) {
    return dataData.map((model) => model.id).sort();
  }
  return [];
}

// src/llm/factory.ts
function normalizeOpenAICompatModel(model, baseUrl) {
  if (baseUrl.includes("openrouter.ai") && model.startsWith("openrouter/")) {
    return model.slice("openrouter/".length);
  }
  return model;
}
function getProviderBaseUrl(provider) {
  return ProviderEndpoints[provider];
}
function getApiKey(provider, options) {
  if (options.apiKeys?.[provider]) {
    return options.apiKeys[provider];
  }
  return options.apiKey || options.defaultApiKey;
}
function createEngine(options) {
  const { model, baseUrl: explicitBaseUrl, apiKeys, defaultApiKey, provider: explicitProvider, ...rest } = options;
  const provider = explicitProvider || detectProviderFromModel(model);
  const baseUrl = explicitBaseUrl || getProviderBaseUrl(provider);
  const apiKey = getApiKey(provider, { apiKeys, defaultApiKey, ...rest });
  const normalizedModel = normalizeOpenAICompatModel(model, baseUrl);
  const config = {
    provider,
    model: normalizedModel,
    apiKey: apiKey || "",
    baseUrl,
    ...rest
  };
  return new OpenAICompatEngine(config);
}
function getEngineType(model, provider) {
  const detected = provider || detectProviderFromModel(model);
  return `OpenAICompatEngine (${detected})`;
}
async function listModels2(provider, apiKey, baseUrl) {
  const resolvedBaseUrl = baseUrl || getProviderBaseUrl(provider);
  return listModels(resolvedBaseUrl, apiKey);
}
async function testConnection(provider, model, apiKey, baseUrl, options) {
  try {
    const engine = createEngine({
      provider,
      model,
      apiKey,
      baseUrl,
      ...options
    });
    const result = await engine.testConnection();
    return {
      success: result.success,
      latencyMs: result.latencyMs,
      error: result.error
    };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Unknown error"
    };
  }
}

// src/parse/parse-blocks.ts
var ASSET_ORDER = [
  "system_prompt",
  "post_history",
  "character_sheet",
  "intro_scene",
  "intro_page",
  "a1111",
  "suno"
];
var ParseError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "ParseError";
  }
};
var DEFAULT_ASSET_ORDER = [
  "system_prompt",
  "post_history",
  "character_sheet",
  "intro_scene",
  "intro_page",
  "a1111"
];
var DEFAULT_ASSET_FILENAMES = {
  system_prompt: "system_prompt.txt",
  post_history: "post_history.txt",
  character_sheet: "character_sheet.txt",
  intro_scene: "intro_scene.txt",
  intro_page: "intro_page.md",
  a1111: "a1111_prompt.txt",
  suno: "suno_prompt.txt"
};
function extractCodeblocks(text) {
  const pattern = /```(?:[a-z]*\n)?(.*?)```/gs;
  const matches = text.match(pattern);
  if (!matches) return [];
  return matches.map((m) => m.trim());
}
function parseBlueprintOutput(text, template) {
  const blocks = extractCodeblocks(text);
  if (blocks.length === 0) {
    throw new ParseError("No codeblocks found in output");
  }
  let startIdx = 0;
  let adjustmentNote;
  if (blocks[0].trim().startsWith("Adjustment Note:")) {
    adjustmentNote = blocks[0].trim();
    startIdx = 1;
  }
  const assetBlocks = blocks.slice(startIdx);
  let expectedAssets = [];
  if (template && template.assets.length > 0) {
    expectedAssets = template.assets.map((a) => a.name);
  } else {
    expectedAssets = [...DEFAULT_ASSET_ORDER];
  }
  const expectedCount = expectedAssets.length;
  if (assetBlocks.length !== expectedCount) {
    const blockPreviews = assetBlocks.slice(0, 3).map((b, i) => `  Block ${i}: ${b.substring(0, 75)}${b.length > 75 ? "..." : ""}`).join("\n");
    let errorMessage = `Expected ${expectedCount} asset blocks, found ${assetBlocks.length}. `;
    errorMessage += `Template requires order: ${expectedAssets.join(", ")}
`;
    errorMessage += `Actual blocks found:
${blockPreviews}`;
    if (assetBlocks.length > 3) {
      errorMessage += `
  ... and ${assetBlocks.length - 3} more blocks`;
    }
    throw new ParseError(errorMessage);
  }
  const assets = {};
  for (let i = 0; i < expectedAssets.length; i++) {
    assets[expectedAssets[i]] = assetBlocks[i];
  }
  const contentFailures = validateAssetsContent(assets);
  if (contentFailures && Object.keys(contentFailures).length > 0) {
    const details = Object.entries(contentFailures).map(([asset, issues]) => `${asset}: ${Array.from(new Set(issues)).join(", ")}`).join("; ");
    throw new ParseError("Generated content failed validation checks: " + details);
  }
  return { assets, adjustmentNote };
}
function extractSingleAsset(output, assetName) {
  const blocks = extractCodeblocks(output);
  if (!blocks.length) {
    throw new ParseError(`No codeblocks found for ${assetName}`);
  }
  let startIdx = 0;
  if (blocks[0].trim().startsWith("Adjustment Note:")) {
    startIdx = 1;
  }
  const assetBlocks = blocks.slice(startIdx);
  if (!assetBlocks.length) {
    throw new ParseError(`No asset block found for ${assetName}`);
  }
  return assetBlocks[0];
}
function extractCharacterName(characterSheet) {
  const displayName = extractCharacterDisplayName(characterSheet);
  if (!displayName) return null;
  return sanitizeCharacterName(displayName);
}
function extractCharacterDisplayName(content) {
  const match = content.match(/^name:\s*(.+)$/m);
  if (!match) return null;
  const name = match[1].trim().replace(/^['"]|['"]$/g, "");
  return name || null;
}
function sanitizeCharacterName(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}
function inferCharacterDisplayNameFromAssets(assets, preferredAssets = ["character_sheet"]) {
  const orderedAssetNames = [];
  const seen = /* @__PURE__ */ new Set();
  for (const asset of preferredAssets) {
    if (asset in assets && !seen.has(asset)) {
      orderedAssetNames.push(asset);
      seen.add(asset);
    }
  }
  for (const asset of Object.keys(assets)) {
    if (!seen.has(asset)) {
      orderedAssetNames.push(asset);
      seen.add(asset);
    }
  }
  for (const assetName of orderedAssetNames) {
    const displayName = extractCharacterDisplayName(assets[assetName] || "");
    if (displayName) return displayName;
  }
  return null;
}
function inferCharacterNameFromAssets(assets, preferredAssets) {
  const displayName = inferCharacterDisplayNameFromAssets(assets, preferredAssets);
  if (!displayName) return null;
  return sanitizeCharacterName(displayName);
}
var PLACEHOLDER_PATTERNS = [
  ["{PLACEHOLDER}", /\{PLACEHOLDER\}/g],
  ["Generic {...} placeholder", /(?<!\{)\{(?!\{)[^{}\n]*[A-Za-z][^{}\n]*\}(?!\})/g],
  ["Suno {TITLE}", /\{TITLE\}/g],
  [
    "Suno other {..}",
    /\{GENRE\}|\{STYLE\}|\{MOOD\}|\{ENERGY\}|\{TEMPO\}|\{BPM\}|\{TEXTURE\}|\{Remaster Style\}/g
  ],
  ["A1111 slot ((...))", /\(\(\.\.\)\)/g],
  [
    "A1111 any slot ((...something...)) left",
    /\(\([^)]*\.\.\.[^)]*\)/g
  ],
  [
    "Character sheet bracket placeholders",
    /\[[A-Za-z][^\]\n]*\]/g
  ]
];
var USER_AUTHORITY_PATTERNS = [
  [
    "Narrates {{user}} action/thought/consent",
    /\{\{user\}\}\s+(?:is|was|feels?|felt|thinks?|thought|decides?|decided|knows?|knows?|wants?|wanted|says?|said|nods?|smiles?|walks?|steps?|looks?|touches?|takes?|gives?|allows?|consents?|agrees?|gasps?|moans?)\b/gi
  ],
  [
    "Narrates {{user}} internal state",
    /\{\{user\}\}'s\s+(?:mind|thoughts?|feelings?|emotions?|desire|consent|decision|reaction|actions?)\b/gi
  ]
];
var INSTRUCTIONAL_USER_REFERENCE_PATTERN = /(?:never|do not|don't|avoid|without)\s+(?:narrat(?:e|ing)|describ(?:e|ing)|assign(?:ing)?)\s*$/gi;
var CHARACTER_SHEET_FILENAME = "character_sheet.txt";
function detectPlaceholderIssues(text, filename) {
  const issues = [];
  for (const [label, pattern] of PLACEHOLDER_PATTERNS) {
    if (label === "Character sheet bracket placeholders" && filename !== CHARACTER_SHEET_FILENAME) {
      continue;
    }
    if (pattern.test(text)) {
      issues.push(label);
    }
  }
  return issues;
}
function detectUserAuthorityIssues(text) {
  const issues = [];
  for (const [label, pattern] of USER_AUTHORITY_PATTERNS) {
    for (const match of text.matchAll(pattern)) {
      const contextBefore = text.substring(Math.max(0, match.index - 48), match.index).trim();
      if (!INSTRUCTIONAL_USER_REFERENCE_PATTERN.test(contextBefore)) {
        issues.push(label);
        break;
      }
    }
  }
  return issues;
}
function validateAssetContent(assetName, content) {
  let filename = `${assetName}.txt`;
  if (assetName === "intro_page") filename = "intro_page.md";
  if (assetName === "character_sheet") filename = CHARACTER_SHEET_FILENAME;
  const issues = detectPlaceholderIssues(content, filename);
  issues.push(...detectUserAuthorityIssues(content));
  return issues;
}
function validateAssetsContent(assets) {
  const failures = {};
  for (const [assetName, content] of Object.entries(assets)) {
    const issues = validateAssetContent(assetName, content);
    if (issues.length > 0) {
      failures[assetName] = issues;
    }
  }
  return Object.keys(failures).length > 0 ? failures : null;
}

// src/export/presets.ts
function applyPreset(assets, preset, characterName = "") {
  const result = {};
  Object.assign(result, preset.metadata);
  if (!characterName && assets.character_sheet) {
    const lines = assets.character_sheet.split("\n").slice(0, 20);
    for (const line of lines) {
      if (line.trim().toLowerCase().startsWith("name:")) {
        characterName = line.split(":", 2)[1].trim();
        break;
      }
    }
  }
  for (const mapping of preset.fields) {
    if (!(mapping.asset in assets)) continue;
    let content = assets[mapping.asset];
    if (!content && mapping.optional) continue;
    if (mapping.wrapper) {
      content = mapping.wrapper.replace(/\{\{content\}\}/g, content).replace(/\{\{char\}\}/g, characterName || "{{char}}").replace(/\{\{user\}\}/g, "{{user}}");
    }
    result[mapping.target] = content;
  }
  if (!("name" in result) && characterName) {
    result.name = characterName;
  }
  return result;
}
function formatExport(data, preset, characterName = "", model = "") {
  let outputName = preset.output_pattern.replace(/\{\{character_name\}\}/g, characterName || "character").replace(/\{\{model\}\}/g, model || "unknown").replace(/\{\{timestamp\}\}/g, "");
  outputName = outputName.replace(/\(\)/g, "").replace(/__/g, "_");
  if (preset.format === "text") {
    const files = [];
    for (const [key, value] of Object.entries(data)) {
      let filename = key;
      if (key.includes(".")) {
        filename = key;
      } else if (key === "intro_page" || key.includes("page")) {
        filename = `${key}.md`;
      } else {
        filename = `${key}.txt`;
      }
      files.push({ filename, content: String(value) });
    }
    return { directory: outputName, files };
  } else if (preset.format === "json") {
    if (!outputName.endsWith(".json")) {
      outputName += ".json";
    }
    return { filename: outputName, content: JSON.stringify(data, null, 2) };
  } else if (preset.format === "combined") {
    if (!outputName.endsWith(".txt")) {
      outputName += ".txt";
    }
    const lines = [];
    for (const [key, value] of Object.entries(data)) {
      lines.push(`=== ${key.toUpperCase()} ===`);
      lines.push("");
      lines.push(String(value));
      lines.push("");
      lines.push("");
    }
    return { filename: outputName, content: lines.join("\n") };
  }
  throw new Error(`Unknown preset format: ${preset.format}`);
}
function validatePreset(preset) {
  const errors = [];
  if (!preset.name) {
    errors.push("Preset name is required");
  }
  if (!["text", "json", "combined"].includes(preset.format)) {
    errors.push(`Invalid format: ${preset.format}`);
  }
  if (!preset.fields.length) {
    errors.push("Preset must have at least one field mapping");
  }
  const validAssets = /* @__PURE__ */ new Set([
    "system_prompt",
    "post_history",
    "character_sheet",
    "intro_scene",
    "intro_page",
    "a1111",
    "suno"
  ]);
  for (const mapping of preset.fields) {
    if (!mapping.asset) {
      errors.push("Field mapping missing asset name");
    }
    if (!validAssets.has(mapping.asset)) {
      errors.push(`Invalid asset name: ${mapping.asset}`);
    }
    if (!mapping.target) {
      errors.push(`Field mapping for ${mapping.asset} missing target`);
    }
  }
  return { isValid: errors.length === 0, errors };
}

// src/import/character-parser.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function detectJsonFormat(data) {
  if (!isRecord(data)) return "unknown";
  if (data.spec === "chara_card_v2" || data.spec_version === "2.0") {
    return "tavernai_v2";
  }
  if (isRecord(data.data) && (data.data.spec === "chara_card_v2" || data.data.spec_version === "2.0")) {
    return "tavernai_v2";
  }
  if (typeof data.creator_notes === "string" && typeof data.first_mes === "string" && typeof data.description === "string") {
    if (Array.isArray(data.tags) && typeof data.creator === "string") {
      return "tavernai_v1";
    }
  }
  if (typeof data.name === "string" && typeof data.description === "string" && typeof data.first_mes === "string") {
    return "tavernai_v1";
  }
  if (isRecord(data.data)) {
    const inner = data.data;
    if (typeof inner.name === "string" && typeof inner.description === "string") {
      return "tavernai_v1";
    }
  }
  return "unknown";
}
function isPngFilename(filename) {
  return filename.toLowerCase().endsWith(".png");
}
function decodeAscii(bytes) {
  let result = "";
  for (let i = 0; i < bytes.length; i++) {
    result += String.fromCharCode(bytes[i]);
  }
  return result;
}
var BASE64_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
function decodeBase64(base64) {
  try {
    const cleaned = base64.replace(/[\s=]+/g, "");
    const len = cleaned.length;
    if (len % 4 !== 0) return null;
    const bytes = [];
    let i = 0;
    while (i < len) {
      const b0 = BASE64_CHARS.indexOf(cleaned[i]);
      const b1 = BASE64_CHARS.indexOf(cleaned[i + 1]);
      const b2 = BASE64_CHARS.indexOf(cleaned[i + 2]);
      const b3 = BASE64_CHARS.indexOf(cleaned[i + 3]);
      if (b0 === -1 || b1 === -1) return null;
      bytes.push(b0 << 2 | b1 >> 4);
      if (b2 !== -1) bytes.push((b1 & 15) << 4 | b2 >> 2);
      if (b3 !== -1) bytes.push((b2 & 3) << 6 | b3);
      i += 4;
    }
    return decodeAscii(new Uint8Array(bytes));
  } catch {
    return null;
  }
}
function extractPngCharaChunk(buffer) {
  const view = new DataView(buffer);
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  for (let i = 0; i < 8; i++) {
    if (view.getUint8(i) !== signature[i]) {
      return null;
    }
  }
  let offset = 8;
  const bytes = new Uint8Array(buffer);
  while (offset < bytes.length) {
    const chunkLength = view.getUint32(offset);
    const chunkType = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7]
    );
    if (chunkType === "tEXt") {
      const chunkData = bytes.slice(offset + 8, offset + 8 + chunkLength);
      const nullIndex = chunkData.indexOf(0);
      if (nullIndex !== -1) {
        const keyword = decodeAscii(chunkData.slice(0, nullIndex));
        if (keyword === "chara") {
          const textData = chunkData.slice(nullIndex + 1);
          const base64Str = decodeAscii(textData);
          return decodeBase64(base64Str);
        }
      }
    }
    offset += 12 + chunkLength;
    if (chunkType === "IEND") break;
  }
  return null;
}
var TAVERNAI_FIELD_MAP = {
  description: "character_sheet",
  personality: "system_prompt",
  first_mes: "intro_scene",
  mes_example: "post_history",
  scenario: "intro_page"
};
var LOREBOOK_SOURCE_KEYS = ["character_book", "lorebook", "world_info"];
function buildReverseMapping(preset) {
  const reverseMap = {};
  for (const field of preset.fields) {
    const cleanTarget = field.target.replace(/\.(txt|md|json)$/i, "");
    reverseMap[cleanTarget] = field.asset;
  }
  return reverseMap;
}
function unwrapContent(content, wrapper) {
  if (!wrapper) return content;
  const charPatterns = [
    /\{\{char\}\}:\s*/g,
    /<START>/gi
  ];
  if (wrapper.includes("<START>")) {
    const lines = content.split("\n");
    const startIndex = lines.findIndex(
      (line) => line.trim().toLowerCase() === "<start>" || line.trim().startsWith("{{user}}:") || line.trim().startsWith("{{char}}:")
    );
    if (startIndex >= 0) {
      for (let i = startIndex; i < lines.length; i++) {
        if (lines[i].trim().startsWith("{{char}}:") || lines[i].trim().startsWith("{{Char}}:")) {
          const charLine = lines[i].replace(/^\{\{[Cc]har\}\}:\s*/, "");
          const remaining = lines.slice(i + 1).join("\n");
          return (charLine + (remaining ? "\n" + remaining : "")).trim();
        }
      }
    }
  }
  return content;
}
function stringifyUnknown(value) {
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value === null || value === void 0) {
    return null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return null;
  }
}
function formatLorebookEntry(entry, index) {
  if (typeof entry === "string") {
    const trimmed = entry.trim();
    return trimmed ? `## Entry ${index}

${trimmed}` : null;
  }
  if (!isRecord(entry)) {
    const serialized = stringifyUnknown(entry);
    return serialized ? `## Entry ${index}

${serialized}` : null;
  }
  const title = typeof entry.name === "string" && entry.name.trim() ? entry.name.trim() : typeof entry.comment === "string" && entry.comment.trim() ? entry.comment.trim() : Array.isArray(entry.keys) && entry.keys.length > 0 ? entry.keys.filter((key) => typeof key === "string" && key.trim().length > 0).join(", ") : `Entry ${index}`;
  const details = [];
  const keys = Array.isArray(entry.keys) ? entry.keys.filter((key) => typeof key === "string" && key.trim().length > 0) : [];
  const secondaryKeys = Array.isArray(entry.secondary_keys) ? entry.secondary_keys.filter((key) => typeof key === "string" && key.trim().length > 0) : [];
  if (keys.length > 0) {
    details.push(`Keys: ${keys.join(", ")}`);
  }
  if (secondaryKeys.length > 0) {
    details.push(`Secondary Keys: ${secondaryKeys.join(", ")}`);
  }
  if (typeof entry.comment === "string" && entry.comment.trim() && entry.comment.trim() !== title) {
    details.push(`Comment: ${entry.comment.trim()}`);
  }
  if (typeof entry.insertion_order === "number") {
    details.push(`Insertion Order: ${entry.insertion_order}`);
  }
  if (typeof entry.enabled === "boolean" && !entry.enabled) {
    details.push("Enabled: false");
  }
  const content = typeof entry.content === "string" && entry.content.trim() ? entry.content.trim() : typeof entry.entry === "string" && entry.entry.trim() ? entry.entry.trim() : typeof entry.text === "string" && entry.text.trim() ? entry.text.trim() : null;
  const bodyParts = [`## ${title}`];
  if (details.length > 0) {
    bodyParts.push(details.join("\n"));
  }
  if (content) {
    bodyParts.push(content);
  } else {
    const serialized = stringifyUnknown(entry);
    if (serialized) {
      bodyParts.push(serialized);
    }
  }
  return bodyParts.join("\n\n").trim();
}
function formatLorebookSource(label, value) {
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    const entries2 = value.map((entry, index) => formatLorebookEntry(entry, index + 1)).filter((entry) => Boolean(entry));
    return entries2.length > 0 ? entries2.join("\n\n") : null;
  }
  if (!isRecord(value)) {
    return stringifyUnknown(value);
  }
  const heading = typeof value.name === "string" && value.name.trim() ? `# ${value.name.trim()}` : `# ${label.replace(/_/g, " ").replace(/\b\w/g, (segment) => segment.toUpperCase())}`;
  const sections = [heading];
  if (typeof value.description === "string" && value.description.trim()) {
    sections.push(value.description.trim());
  }
  const entries = Array.isArray(value.entries) ? value.entries : Array.isArray(value.world_info) ? value.world_info : null;
  if (entries) {
    const formattedEntries = entries.map((entry, index) => formatLorebookEntry(entry, index + 1)).filter((entry) => Boolean(entry));
    if (formattedEntries.length > 0) {
      sections.push(formattedEntries.join("\n\n"));
    }
  }
  if (sections.length === 1) {
    const serialized = stringifyUnknown(value);
    if (serialized) {
      sections.push(serialized);
    }
  }
  return sections.join("\n\n").trim() || null;
}
function extractLorebookAssets(data) {
  const assets = {};
  const sourceKeys = [];
  const sources = [];
  for (const key of LOREBOOK_SOURCE_KEYS) {
    if (data[key] !== void 0 && data[key] !== null) {
      sources.push({ key, value: data[key] });
    }
  }
  if (isRecord(data.extensions)) {
    for (const key of LOREBOOK_SOURCE_KEYS) {
      if (data.extensions[key] !== void 0 && data.extensions[key] !== null) {
        sources.push({ key: `extensions.${key}`, value: data.extensions[key] });
      }
    }
  }
  sources.forEach(({ key, value }, index) => {
    const content = formatLorebookSource(key, value);
    if (!content) {
      return;
    }
    const assetName = index === 0 ? "lorebook" : `lorebook_${index + 1}`;
    assets[assetName] = content;
    sourceKeys.push(key.split(".")[0]);
  });
  return { assets, sourceKeys: [...new Set(sourceKeys)] };
}
function flattenCardData(raw) {
  if (isRecord(raw.data)) {
    return { ...raw.data };
  }
  return raw;
}
function parseTavernAICard(raw) {
  if (!isRecord(raw)) {
    throw new Error("Invalid TavernAI card: expected JSON object");
  }
  const data = flattenCardData(raw);
  const assets = {};
  const unmappedFields = {};
  const lorebook = extractLorebookAssets(data);
  const name = typeof data.name === "string" ? data.name.trim() : "Imported Character";
  for (const [externalField, internalAsset] of Object.entries(TAVERNAI_FIELD_MAP)) {
    const value = data[externalField];
    if (typeof value === "string" && value.trim()) {
      assets[internalAsset] = unwrapContent(value.trim());
    }
  }
  Object.assign(assets, lorebook.assets);
  const mappedKeys = /* @__PURE__ */ new Set([...Object.keys(TAVERNAI_FIELD_MAP), ...lorebook.sourceKeys]);
  const skipKeys = /* @__PURE__ */ new Set(["name", "spec", "spec_version", "data"]);
  for (const [key, value] of Object.entries(data)) {
    if (skipKeys.has(key) || mappedKeys.has(key)) continue;
    if (typeof value === "string" && value.trim()) {
      unmappedFields[key] = value.trim();
    } else if (Array.isArray(value) && value.length > 0) {
      unmappedFields[key] = JSON.stringify(value);
    }
  }
  return {
    name,
    assets,
    sourceFormat: "tavernai_v1",
    sourcePreset: "TavernAI / SillyTavern",
    unmappedFields: Object.keys(unmappedFields).length > 0 ? unmappedFields : void 0
  };
}
function parseChubAICard(raw) {
  const result = parseTavernAICard(raw);
  return {
    ...result,
    sourceFormat: "chubai",
    sourcePreset: "Chub AI"
  };
}
function parseGenericCharacter(raw, filename) {
  if (!isRecord(raw)) {
    throw new Error("Invalid character data: expected JSON object");
  }
  const data = flattenCardData(raw);
  const rawText = JSON.stringify(raw, null, 2);
  const lorebook = extractLorebookAssets(data);
  const nameCandidate = typeof data.name === "string" && data.name.trim() ? data.name.trim() : typeof data.character_name === "string" && data.character_name.trim() ? data.character_name.trim() : filename?.replace(/\.[^.]+$/, "") || "Imported Character";
  return {
    name: nameCandidate,
    assets: {
      character_sheet: rawText,
      ...lorebook.assets
    },
    sourceFormat: "unknown"
  };
}
function parsePlainTextContent(text, filename) {
  const nameMatch = text.match(/^name:\s*(.+)$/m);
  const name = nameMatch?.[1]?.trim() || filename?.replace(/\.[^.]+$/, "") || "Imported Character";
  return {
    name,
    assets: {
      character_sheet: text
    },
    sourceFormat: "plain_text"
  };
}
function safeParseJson(text) {
  try {
    const parsed = JSON.parse(text);
    if (isRecord(parsed)) return parsed;
    return null;
  } catch {
    return null;
  }
}
function safeParseJsonValue(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
function detectAndParseCharacter(data, filename) {
  if (data instanceof ArrayBuffer) {
    if (data.byteLength > 0) {
      const jsonStr = extractPngCharaChunk(data);
      if (jsonStr) {
        const jsonData = safeParseJsonValue(jsonStr);
        if (jsonData && isRecord(jsonData)) {
          try {
            const format = detectJsonFormat(jsonData);
            const result = format === "chubai" ? parseChubAICard(jsonData) : format !== "unknown" ? parseTavernAICard(jsonData) : parseGenericCharacter(jsonData, filename);
            return { ...result, sourceFormat: "png_card" };
          } catch {
          }
        }
        return parsePlainTextContent(jsonStr, filename);
      }
      return parsePlainTextContent(
        `[Binary PNG file: ${filename || "unknown"} \u2014 no embedded character card found]`,
        filename
      );
    }
  }
  if (typeof data !== "string") {
    return parsePlainTextContent(
      `[Unsupported data format]`,
      filename
    );
  }
  const trimmed = data.trim();
  if (!trimmed) {
    return parsePlainTextContent(
      `[Empty file]`,
      filename
    );
  }
  const jsonObj = safeParseJson(trimmed);
  if (jsonObj) {
    try {
      const format = detectJsonFormat(jsonObj);
      switch (format) {
        case "tavernai_v1":
          return parseTavernAICard(jsonObj);
        case "tavernai_v2":
          return parseTavernAICard(jsonObj);
        case "chubai":
          return parseChubAICard(jsonObj);
        case "unknown":
        default:
          return parseGenericCharacter(jsonObj, filename);
      }
    } catch {
    }
  }
  return parsePlainTextContent(trimmed, filename);
}
function formatSourceLabel(format) {
  switch (format) {
    case "tavernai_v1":
      return "TavernAI v1";
    case "tavernai_v2":
      return "TavernAI v2 / SillyTavern";
    case "chubai":
      return "Chub AI";
    case "png_card":
      return "PNG Character Card";
    case "plain_text":
      return "Plain Text";
    case "unknown":
    default:
      return "Unknown Format";
  }
}

// src/templates/index.ts
var OFFICIAL_TEMPLATE = {
  name: "V2/V3 Card",
  version: "3.1",
  description: "Built-in character card template with 6 standard assets",
  is_official: true,
  assets: [
    {
      name: "system_prompt",
      required: true,
      depends_on: [],
      description: "System-level behavioral instructions",
      blueprint_file: "blueprints/system/system_prompt.md"
    },
    {
      name: "post_history",
      required: true,
      depends_on: ["system_prompt"],
      description: "Conversation context and relationship state",
      blueprint_file: "blueprints/system/post_history.md"
    },
    {
      name: "character_sheet",
      required: true,
      depends_on: ["system_prompt", "post_history"],
      description: "Structured character data",
      blueprint_file: "blueprints/system/character_sheet.md"
    },
    {
      name: "intro_scene",
      required: true,
      depends_on: ["system_prompt", "post_history", "character_sheet"],
      description: "First interaction scenario",
      blueprint_file: "blueprints/system/intro_scene.md"
    },
    {
      name: "intro_page",
      required: true,
      depends_on: ["character_sheet"],
      description: "Visual character introduction page",
      blueprint_file: "blueprints/system/intro_page.md"
    },
    {
      name: "a1111",
      required: true,
      depends_on: ["character_sheet"],
      description: "Stable Diffusion image generation prompt",
      blueprint_file: "blueprints/system/a1111.md"
    }
  ]
};
var DEFAULT_ASSET_ORDER2 = [
  "system_prompt",
  "post_history",
  "character_sheet",
  "intro_scene",
  "intro_page",
  "a1111"
];
function topologicalSort(assets) {
  const assetNames = assets.map((a) => a.name);
  const resolved = [];
  const resolvedSet = /* @__PURE__ */ new Set();
  const visiting = /* @__PURE__ */ new Set();
  function visit(assetName) {
    if (resolvedSet.has(assetName)) return;
    if (visiting.has(assetName)) {
      return;
    }
    visiting.add(assetName);
    const asset = assets.find((a) => a.name === assetName);
    if (asset) {
      for (const dep of asset.depends_on) {
        visit(dep);
      }
    }
    resolvedSet.add(assetName);
    resolved.push(assetName);
    visiting.delete(assetName);
  }
  for (const assetName of assetNames) {
    if (!resolvedSet.has(assetName)) {
      visit(assetName);
    }
  }
  return resolved;
}
function getOrderedAssets(template) {
  const targetTemplate = template || OFFICIAL_TEMPLATE;
  const orderedNames = topologicalSort(targetTemplate.assets);
  return orderedNames.map((name) => targetTemplate.assets.find((a) => a.name === name)).filter((a) => a !== void 0);
}
function validateTemplate(template) {
  const errors = [];
  if (!template.name) {
    errors.push("Template name is required");
  }
  if (!template.assets || template.assets.length === 0) {
    errors.push("Template must have at least one asset");
  }
  const assetMap = new Map(template.assets.map((a) => [a.name, a]));
  function checkDeps(deps, currentAssetName) {
    for (const dep of deps) {
      if (dep === currentAssetName) {
        return true;
      }
      const depAsset = assetMap.get(dep);
      if (depAsset && checkDeps(depAsset.depends_on, currentAssetName)) {
        return true;
      }
    }
    return false;
  }
  for (const asset of template.assets) {
    if (checkDeps(asset.depends_on, asset.name)) {
      errors.push(`Circular dependency detected for asset: ${asset.name}`);
    }
  }
  return { isValid: errors.length === 0, errors };
}

// src/blueprint/orchestrator.ts
function getOfficialTemplate() {
  return {
    name: "V2/V3 Card",
    version: "3.1",
    description: "Built-in character card template with 6 standard assets",
    is_official: true,
    assets: [
      {
        name: "system_prompt",
        required: true,
        depends_on: [],
        description: "System-level behavioral instructions",
        blueprint_file: "blueprints/system/system_prompt.md"
      },
      {
        name: "post_history",
        required: true,
        depends_on: ["system_prompt"],
        description: "Conversation context and relationship state",
        blueprint_file: "blueprints/system/post_history.md"
      },
      {
        name: "character_sheet",
        required: true,
        depends_on: ["system_prompt", "post_history"],
        description: "Structured character data",
        blueprint_file: "blueprints/system/character_sheet.md"
      },
      {
        name: "intro_scene",
        required: true,
        depends_on: ["system_prompt", "post_history", "character_sheet"],
        description: "First interaction scenario",
        blueprint_file: "blueprints/system/intro_scene.md"
      },
      {
        name: "intro_page",
        required: true,
        depends_on: ["character_sheet"],
        description: "Visual character introduction page",
        blueprint_file: "blueprints/system/intro_page.md"
      },
      {
        name: "a1111",
        required: true,
        depends_on: ["character_sheet"],
        description: "Stable Diffusion image generation prompt",
        blueprint_file: "blueprints/system/a1111.md"
      }
    ]
  };
}
function buildModeInstruction(mode) {
  if (mode === "SFW") {
    return `Mode: SFW
No explicit sexual content; fade to black if sexuality is implied.`;
  } else if (mode === "Platform-Safe") {
    return `Mode: Platform-Safe
No explicit sexual content and avoid platform-risky extremes; preserve tension through behavior, leverage, or emotional pressure instead.`;
  } else if (mode === "Auto") {
    return `Mode: Auto
Infer the content mode from the seed when obvious; otherwise default to NSFW.`;
  }
  return `Mode: NSFW
Explicit sexual content is allowed only if it fits the seed.`;
}
function buildOrchestrator(options = {}) {
  const { mode, template, additionalInstructions } = options;
  const targetTemplate = template || getOfficialTemplate();
  const assetNames = targetTemplate.assets.map((a) => a.name);
  const assetList = assetNames.join("\n");
  const modeInstruction = buildModeInstruction(mode);
  return `---
name: Generator Orchestrator
description: Compile a full suite of character assets from a single seed.
invokable: true
always: false
version: 3.1
---

# Generator Orchestrator

You do not write disconnected snippets.
You compile a character package.

Your input is a single SEED.
Your output is a coherent set of assets that exactly matches the active template contract.

Think like a compiler:

- Deterministic
- Structured
- Coherent across assets
- No improvisation outside of requested schema

## Primary Function

Compile the active template contract.

If no active template contract appears later in this prompt, use the fallback official asset order:

${assetList}

If a TEMPLATE OVERRIDE section or other active template contract appears later in this prompt, that contract supersedes the fallback asset set and output order.

When an active template contract is present:

- Generate only the assets named in the contract.
- Follow the declared asset order exactly.
- Respect the declared dependency order.
- Ignore fallback-only assets that are not part of the active contract.

Every generated asset must describe the same character and preserve the same:

- Psychology
- Power dynamic
- Emotional core
- Sensory identity
- Posture toward {{user}}

No asset may contradict another.

## Role Definition

You are a world-building compiler, not a narrator.

- Translate the seed into behavioral logic and platform-ready assets.
- Make concrete, defensible choices when the seed is thin.
- Prefer coherence over novelty.
- Do not explain your choices.

## Content Mode

${modeInstruction}

If the user does not specify a mode, infer it when obvious; otherwise default to NSFW.
If mode is included inline, such as \`Mode: SFW\`, treat it as explicitly specified.

## Hierarchy Of Authority

For the active template, authority flows according to the declared dependency graph, not a fixed universal asset ladder.

- Upstream assets define identity, behavioral logic, and any facts later assets must honor.
- Midstream assets refine relationship state, profile structure, opener context, or world logic only within the scope allowed by their dependencies.
- Downstream assets translate already-established facts into later views such as scenes, pages, openers, or media prompts.
- Assets that share the same dependency tier must stay mutually consistent and may not invent facts their siblings would have required upstream.

Lower-tier assets may not override higher-tier logic.

## Format Compliance

Blueprint formatting is mandatory.

You must:

- Follow each asset blueprint exactly.
- Preserve exact section names and field names.
- Output all required control blocks and metadata sections.
- Keep module-specific formats module-specific.
- Use \`{{user}}\` verbatim.
- Never assign actions, thoughts, dialogue, emotions, sensations, decisions, or consent to \`{{user}}\`.
- Never invent consent.

You must not:

- Normalize different asset formats into one shared style.

Fatal failures include:

- Character sheet not matching its required field structure.
- A1111 simplified into a loose prompt instead of full control layout.
- Leaving placeholders unresolved.
- Outputting extra commentary outside asset codeblocks.

## Character Sheet Reminder

When the active template includes \`character_sheet\`, it must start with these exact field headers:

\`\`\`text
name: [character name]
age: [age]
occupation: [occupation]
heritage: [heritage]
\`\`\`

Follow the rest of \`character_sheet\` blueprint exactly after that.

Do not use alternate card schemas such as \`[Character]\`, \`[Profile]\`, W++, or merged attribute lines.

When the active template uses split profile assets instead of \`character_sheet\`, follow each local asset blueprint exactly and do not collapse the template back into a legacy single-card schema.

## Output Rules

- Output one asset per codeblock or file.
- Output assets in the active template order.
- Output nothing outside of codeblocks except optional Adjustment Note codeblock.
- Plaintext unless asset blueprint explicitly requires Markdown or another format.
- For \`system_prompt\` and \`post_history\`, keep output paragraph-only with no headings or bullets.
- Use \`{{user}}\` verbatim.
- Never assign actions, thoughts, dialogue, emotions, sensations, decisions, or consent to \`{{user}}\`.
- Never invent consent.

## Emotional Coherence

All assets must express the same core emotional truth.

Use this invariant chain:

CORE THEME
\u2192 recurring behavioral pattern
\u2192 mirrored sensory detail
\u2192 consistent emotional pressure on {{user}}

No tonal drift.

## Anti-Generic Enforcement

Do not default to:

- Chosen-one framing
- Prophecy shortcuts
- Secret royalty shortcuts
- Blank-slate perfection
- Decorative trauma without behavioral consequence
- Stock cold-but-secretly-soft shortcuts unless the seed explicitly earns it

Characters should feel:

- Contradictory
- Operationally flawed
- Behaviorally legible
- Difficult in ways that matter

Every character should have:

- A meaningful flaw that creates friction
- At least two competing internal drives
- One unexpected competence or fixation
- One trait that creates problems rather than solving them
- A reason they cannot cleanly disengage from {{user}}

## Style Directives

- Show behavior, not adjective piles.
- Use concrete sensory anchors.
- Prefer subtext over explanation.
- End scenes with tension, not closure.
- Treat \`{{user}}\` as catalyst, not audience.

## Genre Adaptation

- Romance or slice-of-life: warmer cues, tactile comfort, slower escalation, explicit respect for boundaries.
- Thriller or noir: clipped pacing, leverage, suspicion, asymmetry.
- Horror: dominant sensory detail, restraint on exposition, vulnerability as hook.
- Fantasy: concrete rules, tactile worldbuilding, grounded stakes.
- Sci-fi or cyberpunk: technology as texture, not infodump; keep terminology lean.
- Comedy or lighthearted: rhythm, missteps, and charm without erasing flaws or stakes.

## Issue Handling

- If you detect contradictions, resolve them using hierarchy. Higher-tier logic wins.
- If a constraint cannot be perfectly satisfied, emit an Adjustment Note codeblock and deliver the best coherent result anyway.
- Do not stop at an error line. Always produce usable assets.

## Final Consistency Check

Before output, verify internally:

- Core identity is visible across all assets.
- Central fear appears behaviorally at least twice.
- Sensory signature recurs across multiple assets.
- Output count and order match the active template contract exactly.
- No assets outside the active template contract are emitted.

## Mission Statement

You are assembling one character through multiple constrained views.
Every asset is a different lens on the same underlying person.
Make them align.

${additionalInstructions ? `

## Additional Instructions

${additionalInstructions}` : ""}
`;
}
function getDefaultAssetOrder() {
  return DEFAULT_ASSET_ORDER2;
}
export {
  APIError,
  ASSET_ORDER,
  DEFAULT_ASSET_FILENAMES,
  EidolonAPI,
  GenerationStream,
  getDefaultAssetOrder as GetDefaultAssetOrder,
  MAX_CONNECTED_DRAFT_REFERENCES,
  OFFICIAL_TEMPLATE,
  OpenAICompatEngine,
  ParseError,
  ProviderAuthHeaders,
  ProviderEndpoints,
  DEFAULT_ASSET_ORDER2 as TemplateAssetOrder,
  api,
  applyPreset,
  buildOrchestrator,
  buildReverseMapping,
  createEngine,
  detectAndParseCharacter,
  detectJsonFormat,
  detectProviderFromModel,
  extractCharacterDisplayName,
  extractCharacterName,
  extractCodeblocks,
  extractPngCharaChunk,
  extractSingleAsset,
  formatAuthHeader,
  formatExport,
  formatSourceLabel,
  getEngineType,
  getOfficialTemplate,
  getOrderedAssets,
  inferCharacterDisplayNameFromAssets,
  inferCharacterNameFromAssets,
  isPngFilename,
  listModels2 as listModels,
  parseBlueprintOutput,
  parseChubAICard,
  parseGenericCharacter,
  parsePlainTextContent,
  parseTavernAICard,
  sanitizeCharacterName,
  testConnection,
  topologicalSort,
  validateAssetContent,
  validateAssetsContent,
  validatePreset,
  validateTemplate
};
//# sourceMappingURL=index.js.map